import { defineComponent as _defineComponent } from 'vue'
import Cleave from "vue-cleave-component";
  import { reactive, computed, ref, onBeforeMount } from "vue";
  import useVuelidate from "@vuelidate/core";
  import { email, required } from "@vuelidate/validators";
  import store from "@/store";
  import router from "@/router";
  import { validPhoneNumber } from "@/helpers/validation";
  import { phoneNumberOption } from "@/helpers/cleavePlugin";
  import SelectProjectsModal from "@/components/ui/modals/SelectProjectsModal.vue";
  import { POSITION, useToast } from "vue-toastification";
  import { EstateInterface } from "@/types/EstateInterface";
  import { SalesPersonInterface } from "@/types/SalesPersonInterface";

  interface SalesPersonForm {
    firstName: string;
    lastName: string;
    email?: string;
    phone: string;
    estateIds: string[];
    verifiedEmail?: boolean;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'SalesPersonCreateProfile',
  setup(__props, { expose: __expose }) {
  __expose();

  const toast = useToast();
  const createSalesPerson = ref(true);
  const showSelectProjectsModal = ref(false);
  const selectProjectsBtnAvailable = ref(true);
  const loading = ref(false);
  const selectedProjects = ref<EstateInterface[]>([]);
  const projects = ref<EstateInterface[]>([]);
  const userData = ref<SalesPersonInterface>();

  const form = reactive({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    estateIds: [],
  }) as SalesPersonForm;

  const rules = computed(() => {
    return {
      form: {
        firstName: { required },
        lastName: { required },
        email: { required, email },
        phone: { validPhoneNumber },
        estateIds: [],
      },
    };
  });

  const isCreatePage = computed(() => {
    return router.currentRoute.value.name === "LandownerUsersCreate";
  });

  const selectedProjectsIds = computed(() => {
    return selectedProjects.value.map((item: EstateInterface) => item._id);
  });

  const getLandOwnerId = computed(() => {
    return store.state.user.data._id;
  });

  const v$ = useVuelidate(rules, { form });

  const selectProjects = () => {
    showSelectProjectsModal.value = true;
  };

  onBeforeMount(async () => {
    await getProjects();

    if (!isCreatePage.value) {
      await getSalesPerson();
    }

    selectProjectsBtnAvailable.value = true;
    loading.value = false;
  });

  const getProjects = () => {
    selectProjectsBtnAvailable.value = false;
    loading.value = true;
    return store
      .dispatch("estate/getEstates", {
        landownerId: getLandOwnerId.value,
      })
      .then((res) => {
        projects.value = res.data;
        return setIsSelectedProjects();
      });
  };

  const getSearchQueryProjects = (searchQuery: string) => {
    store
      .dispatch("estate/getEstates", {
        title: searchQuery,
        landownerId: getLandOwnerId.value,
      })
      .then((res) => {
        projects.value = res.data;

        if (selectedProjects.value && selectedProjects.value.length) {
          setSelectedProjectsAfterSearch(selectedProjectsIds.value);
        }
      });
  };

  const getSalesPerson = async () => {
    return store
      .dispatch("user/getUser", router.currentRoute.value.params.salesPersonId)
      .then((res) => {
        form.firstName = res.data.firstName;
        form.lastName = res.data.lastName;
        form.email = res.data.email;
        form.phone = res.data.phone;
        setSelectedProjects(res.data.detailsId.estateIds);
        userData.value = res.data;
        return res.data;
      })
      .catch(() => {
        toast.error("Failed to update the user. Please contact support.");
      });
  };

  const setSelectedProjectsAfterSearch = (selectedProjectsIdsArr: string[]) => {
    selectedProjectsIdsArr.forEach((id) => {
      const index = projects.value.findIndex(
        (item: EstateInterface) => item._id === id
      );

      if (index >= 0) {
        projects.value[index].isSelected = true;
      }
    });
  };

  const setIsSelectedProjects = () => {
    projects.value.forEach((item: EstateInterface) => {
      item.isSelected = false;
    });
  };

  const setSelectProjects = () => {
    selectedProjects.value = projects.value.filter(
      (item: EstateInterface) => item.isSelected
    );
  };

  const setSelectedProjects = (estateIds: string[]) => {
    selectedProjects.value = [];
    estateIds.forEach((id) => {
      selectedProjects.value.push(
        projects.value.find((item: EstateInterface) => item._id === id)
      );
      const index = projects.value.findIndex(
        (item: EstateInterface) => item._id === id
      );

      if (index >= 0) {
        projects.value[index].isSelected = true;
      }
    });
  };

  const removeProject = (id: string) => {
    const index = projects.value.findIndex(
      (item: EstateInterface) => item._id === id
    );

    if (index >= 0) {
      projects.value[index].isSelected = false;
    }

    setSelectProjects();
  };

  const updateFormIfEditMode = () => {
    const formCopy = form;

    if (!isCreatePage.value && userData.value?.email !== form.email) {
      formCopy.verifiedEmail = false;
    } else if (!isCreatePage.value && userData.value?.email === form.email) {
      delete formCopy.email;
    }

    return formCopy;
  };

  const save = () => {
    if (form.email) {
      form.email = form.email.toLowerCase();
    }

    v$.value.$touch();

    if (v$.value.$invalid) {
      return;
    }

    loading.value = true;
    form.estateIds = selectedProjectsIds.value;
    const actions = {
      createSalesPerson: "createSalesPerson",
      updateSalesPersonByLandowner: "updateSalesPersonByLandowner",
    };

    if (isCreatePage.value) {
      createOrUpdateSalesPerson(actions.createSalesPerson);
    } else {
      createOrUpdateSalesPerson(actions.updateSalesPersonByLandowner);
    }
  };

  const createOrUpdateSalesPerson = (action: string) => {
    store
      .dispatch(
        `user/${action}`,
        isCreatePage.value
          ? form
          : { userId: userData.value?._id, form: updateFormIfEditMode() }
      )
      .then(() => {
        router.push({ name: "LandownerUsers" });
      })
      .catch((error) => {
        toast.error(error.response.data.message, {
          position: POSITION.TOP_CENTER,
        });
      })
      .finally(() => {
        loading.value = false;
      });
  };

const __returned__ = { toast, createSalesPerson, showSelectProjectsModal, selectProjectsBtnAvailable, loading, selectedProjects, projects, userData, form, rules, isCreatePage, selectedProjectsIds, getLandOwnerId, v$, selectProjects, getProjects, getSearchQueryProjects, getSalesPerson, setSelectedProjectsAfterSearch, setIsSelectedProjects, setSelectProjects, setSelectedProjects, removeProject, updateFormIfEditMode, save, createOrUpdateSalesPerson, get Cleave() { return Cleave }, reactive, computed, ref, onBeforeMount, get useVuelidate() { return useVuelidate }, get email() { return email }, get required() { return required }, get store() { return store }, get router() { return router }, get validPhoneNumber() { return validPhoneNumber }, get phoneNumberOption() { return phoneNumberOption }, SelectProjectsModal, get POSITION() { return POSITION }, get useToast() { return useToast }, get EstateInterface() { return EstateInterface }, get SalesPersonInterface() { return SalesPersonInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})